import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Building2, Camera, ChevronRight, LogOut, Menu, Pencil, X } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { NotificationsBell } from "../NotificationsBell";
import { PinSearch } from "../PinSearch";
import { StaffAvatar, EmpCode } from "../StaffAvatar";
import { ProfilePhotoDialog } from "../AvatarEditor";
import { SuperProfileDialog } from "../SuperProfileDialog";
import { OPEN_ACCOUNT_EVENT } from "../TwoStepBanner";
import { AssistantWidget } from "../assistant/AssistantWidget";   // Phase 171
import { RoleSwitcher } from "../RoleSwitcher";   // Phase 174: one login, several roles

const STAFF = ["municipal_admin", "supervisor", "tax_collector"];

const linkClass = (isActive, sub = false) =>
  `flex items-center rounded-lg font-medium transition-colors ${sub ? "gap-2.5 px-2.5 py-2 text-[13px] leading-snug" : "gap-3 px-3 py-2.5 text-sm"} ${
    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
  }`;

const isPathActive = (pathname, item) => (item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(item.to + "/"));

/** Phase 190 — optional count pill on a sidebar entry (e.g. open Flagged Payments). Hidden when 0 / undefined. */
const NavBadge = ({ count, testId }) => {
  if (!count || count <= 0) return null;
  return (
    <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-semibold tabular-nums leading-none"
      data-testid={`nav-${testId}-badge`} aria-label={`${count} pending`}>{count > 99 ? "99+" : count}</span>
  );
};

/** Phase 179 — a sidebar group (e.g. Admin › Reports): one parent row that expands / collapses its sub-menu on click.
 *  Opens by itself when one of its pages is the current page; the choice is remembered per group in localStorage. */
const NavGroup = ({ item, onNavigate }) => {
  const { pathname } = useLocation();
  const childActive = item.children.some((c) => isPathActive(pathname, c));
  const storageKey = `nav-group:${item.testId}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved === "1") return true;
      if (saved === "0") return childActive;   // user collapsed it — stay collapsed unless we are inside it
    } catch (e) { /* storage unavailable */ }
    return childActive;
  });
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  const toggle = () => {
    setOpen((o) => {
      try { window.localStorage.setItem(storageKey, o ? "0" : "1"); } catch (e) { /* ignore */ }
      return !o;
    });
  };
  return (
    <div data-testid={`nav-${item.testId}-group`} data-state={open ? "open" : "closed"}>
      <button type="button" onClick={toggle} aria-expanded={open} aria-controls={`nav-${item.testId}-submenu`} data-testid={`nav-${item.testId}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          childActive && !open ? "bg-muted text-foreground" : open ? "text-foreground hover:bg-muted" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`}>
        <item.icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        {!open && <NavBadge count={item.children.reduce((s, c) => s + (c.badge || 0), 0)} testId={`${item.testId}-group`} />}
        <ChevronRight className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`} aria-hidden="true" data-testid={`nav-${item.testId}-chevron`} />
      </button>
      {open && (
        <div id={`nav-${item.testId}-submenu`} role="group" aria-label={`${item.label} sub-menu`} className="mt-1 ml-4 pl-2 border-l border-border flex flex-col gap-0.5" data-testid={`nav-${item.testId}-submenu`}>
          {item.children.map((c) => (
            <NavLink key={c.to} to={c.to} end={c.end} onClick={onNavigate} data-testid={`nav-${c.testId}`} className={({ isActive }) => linkClass(isActive, true)}>
              <c.icon className="h-3.5 w-3.5 shrink-0" />
              {c.label}
              <NavBadge count={c.badge} testId={c.testId} />
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export const AppLayout = ({ navItems, title, subtitle, children, code, hideSearch = false }) => {
  const { auth, logout, updateUser, login } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const me = auth?.user;
  // Phase 86: staff can change their own photo from the identity block
  const canEditPhoto = !!code && STAFF.includes(me?.role);
  // Platform Super Admin: the identity block opens "My account" (photo, name, mobile, sign-in email, password)
  const isSuper = me?.role === "super_admin";
  const identityClickable = canEditPhoto || isSuper;
  const openIdentity = () => { if (isSuper) setProfileOpen(true); else if (canEditPhoto) setPhotoOpen(true); };
  const identityTitle = isSuper ? "My account — photo, mobile, email, password, security" : canEditPhoto ? "Change my photo" : undefined;
  // pages (e.g. the two-step banner on the dashboard) can ask the shell to open My account
  useEffect(() => {
    if (!isSuper) return undefined;
    const h = () => setProfileOpen(true);
    window.addEventListener(OPEN_ACCOUNT_EVENT, h);
    return () => window.removeEventListener(OPEN_ACCOUNT_EVENT, h);
  }, [isSuper]);
  // employee codes exist for tenant staff only (never for citizens or the platform Super Admin)
  const showEmpCode = !!me?.role && !["citizen", "super_admin"].includes(me.role);
  // Phase 174: number of active role accounts this person holds (sidebar hint; switcher lives in the header)
  const multiRoleCount = (auth?.roles || []).filter((r) => r.is_active !== false).length;

  const doLogout = () => {
    logout();
    navigate("/");
  };

  // Super Admin saved their profile: patch the session (and swap in the refreshed token when the name changed)
  const onSuperSaved = (userPatch, token) => {
    if (token) login({ ...auth, token, user: { ...auth.user, ...userPatch } });
    else updateUser(userPatch);
  };

  const nav = (
    <nav className="flex flex-col gap-1 px-3">
      {navItems.map((item) => item.children ? (
        <NavGroup key={item.testId} item={item} onNavigate={() => setMobileOpen(false)} />
      ) : (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setMobileOpen(false)}
          data-testid={`nav-${item.testId}`}
          className={({ isActive }) => linkClass(isActive)}
        >
          <item.icon className="h-4 w-4 shrink-0" />
          {item.label}
          <NavBadge count={item.badge} testId={item.testId} />
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 flex-col border-r bg-card fixed inset-y-0">
        <div className="px-4 py-4 border-b">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm font-heading truncate">{title}</p>
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-4">{nav}</div>
        <div className="border-t px-4 py-3">
          <button type="button"
            className={`flex items-center gap-2.5 min-w-0 w-full text-left rounded-lg -mx-1 px-1 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${identityClickable ? "hover:bg-muted cursor-pointer" : "cursor-default"}`}
            onClick={openIdentity} disabled={!identityClickable} title={identityTitle} data-testid="sidebar-identity-button">
            <span className="relative rounded-full shrink-0" data-testid="sidebar-my-avatar">
              <StaffAvatar code={code} userId={me?.id} name={me?.name} photoUrl={me?.photo_url ?? null} size="md" />
              {identityClickable && (
                <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center ring-2 ring-card" aria-hidden="true">
                  {isSuper ? <Pencil className="h-2.5 w-2.5" /> : <Camera className="h-2.5 w-2.5" />}
                </span>
              )}
            </span>
            <span className="min-w-0 block">
              <span className="block text-sm font-medium truncate" data-testid="sidebar-user-name">{me?.name}{showEmpCode ? <EmpCode code={me?.employee_id} /> : null}</span>
              <span className="block text-xs text-muted-foreground capitalize truncate" data-testid="sidebar-user-role">
                {me?.custom_role_name || me?.role?.replace(/_/g, " ")}
                {multiRoleCount > 1 && <span className="normal-case ml-1 text-[10px] text-primary" data-testid="sidebar-multi-role-count">· {multiRoleCount} roles</span>}
              </span>
            </span>
          </button>
          <Button variant="outline" size="sm" className="w-full mt-2" onClick={doLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-card border-r flex flex-col">
            <div className="px-4 py-4 border-b flex items-center justify-between">
              <p className="font-semibold text-sm font-heading">{title}</p>
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto py-4">{nav}</div>
            <div className="border-t px-4 py-3">
              <Button variant="outline" size="sm" className="w-full" onClick={doLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        <header data-app-header className="sticky top-0 z-40 bg-background/80 backdrop-blur border-b px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
            <p className="text-sm text-muted-foreground hidden sm:block">{subtitle}</p>
          </div>
          <div className="flex items-center gap-1">
            {code && !hideSearch && me?.role !== "super_admin" && <PinSearch code={code} />}
            {(code || isSuper) && <NotificationsBell code={code} />}
            {code && STAFF.includes(me?.role) && <RoleSwitcher code={code} />}
            <button type="button" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
              onClick={openIdentity} disabled={!identityClickable} title={identityTitle} data-testid="header-my-avatar">
              <StaffAvatar code={code} userId={me?.id} name={me?.name} photoUrl={me?.photo_url ?? null} size="sm" />
              <span className="text-sm font-medium hidden sm:block" data-testid="header-user-name">{me?.name}{showEmpCode ? <EmpCode code={me?.employee_id} /> : null}</span>
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
      {canEditPhoto && (
        <ProfilePhotoDialog code={code} open={photoOpen} onOpenChange={setPhotoOpen} user={me}
          onChanged={(url) => updateUser({ photo_url: url })} />
      )}
      {isSuper && (
        <SuperProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={me} onSaved={onSuperSaved} />
      )}
      {code && !isSuper && <AssistantWidget code={code} />}
    </div>
  );
};
